import React, {useEffect, useState} from "react";
import cn from 'classnames';
import {useModal} from '@helpers/hooks/useModal';
import {useNavigate} from "react-router-dom";
import uniqid from "uniqid";
import axios from "axios";
import {
    addImageFromSocial,
    getImagesDB,
} from '@api';
import { enqueueResilientUpload, removeBlobEntry, uploadBaseForAttempt } from "@helpers/uploadResilience";
import {
    imageCountState
} from "@atoms/priceCalc";
import {loaderState, imagesBlobState} from "@atoms/index";
import {useRecoilState, useRecoilValue} from "recoil";
import "./style.css";
import {imageUploadState} from "../../../Recoil/Atoms";
import { createFaceCenteredCrop, warmupFaceDetection } from "../../../../utils/faceDetection";
import { downscaleForUpload } from "../../../../utils/uploadDownscale";
import UploadErrorSnackbar from "../../components/UploadErrorSnackbar";
import { getCartDiscountEvent } from "../../../api/activePromoEvent";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const UploadOptions = ({isOpen, style}) => {
    const imagecount = useRecoilValue(imageCountState);
    const [, setLoader] = useRecoilState(loaderState);
    const [imagesBlob, setImagesBlob] = useRecoilState(imagesBlobState);
    const [uploadImages, setUploadImages] = useRecoilState(imageUploadState);

    const modal = useModal();
    const filestack = require("filestack-js");
    const navigate = useNavigate();
    const FILESTACK_APIKEY = process.env.REACT_APP_FILESTACK_APIKEY;
    // filestack.init throws if the key is missing. Guard it so an unset
    // REACT_APP_FILESTACK_APIKEY (e.g. not baked into the build) degrades the
    // social-import buttons instead of white-screening the whole app on render.
    let filestack_client = null;
    try {
      filestack_client = filestack.init(FILESTACK_APIKEY);
    } catch (e) {
      console.error("Filestack init failed — REACT_APP_FILESTACK_APIKEY missing at build time?", e);
    }
    const [errorImages, setErrorImages] = useState([]);
    const [uploadErrors, setUploadErrors] = useState([]);
    let uid;
    const userId = localStorage.getItem('uniqueUserId');
    if (!userId) {
        uid = uniqid();
        localStorage.setItem("uniqueUserId", uid);
    } else {
        uid = localStorage.getItem("uniqueUserId");

    }

    useEffect(() => {
        const filteredArray = imagesBlob.filter((blob) => !errorImages.includes(blob.id));
        setImagesBlob(() => [
            ...filteredArray,
        ]);
    }, [errorImages]);

    // Pre-warm face detection models when component mounts
    useEffect(() => {
        if (isOpen) {
            warmupFaceDetection().catch(err => {
                console.warn('[UploadOptions] Face detection warmup failed:', err.message);
            });
        }
    }, [isOpen]);

    const hideUploadOptions = () => {
        modal('hide', 'uploadOptions', {}, 300)
    }

    const blobs = (res) => {
        const newImages = [];
        Object.values(res).forEach(val => val.forEach(r => {
            const imageUniqueId = uniqid();
            const imageUploadedAt = new Date().getTime();
            newImages.push({url: r.url, id: imageUniqueId, uploadedAt: imageUploadedAt});
            setImagesBlob((oldImagesBlob) => [
                ...oldImagesBlob,
                {
                    url: r.url,
                    id: imageUniqueId,
                    uploadedAt: imageUploadedAt,
                },
            ]);
            r.uniqueId = imageUniqueId;
            r.uploadedAt = imageUploadedAt;
        }));
        return newImages;
    }

    // Function to generate a session ID via server API
    const generateSessionId = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BASE_URL}/generate-session-id`);
            const data = await response.json();
            if (data && data.sessionId) {
                console.log(`Server generated session ID: ${data.sessionId}`);
                return data.sessionId;
            } else {
                throw new Error('Invalid response from session generation API');
            }
        } catch (error) {
            console.error('Failed to generate session ID from server:', error.message);
            
            // Fallback to client-side generation with new format
            const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
            let fallbackId = 'b';
            for (let i = 0; i < 5; i++) {
                fallbackId += chars[Math.floor(Math.random() * chars.length)];
            }
            console.log(`Generated fallback session ID: ${fallbackId}`);
            return fallbackId;
        }
    };

    // Function to create a cart in the addtocart collection
    const createCart = async (uid, sessionId) => {
        try {
            const userEmail = localStorage.getItem("userEmail") || "";
            const fullName = localStorage.getItem("fullName") || "";
            const logIdentifier = userEmail ? `[${uid}-${userEmail}]` : `[${uid}]`;
            
            console.log(`${logIdentifier} Creating cart with sessionId: ${sessionId}`);
            
            await fetch(`${process.env.REACT_APP_BASE_URL}/addtocart`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    uid: uid,
                    sessionId: sessionId,
                    userInfo: {
                        fullName: fullName,
                        email: userEmail
                    },
                    frameType: "classic", // Default frame type
                    discountEvent: getCartDiscountEvent()
                }),
            });
            
            console.log(`${logIdentifier} Cart created successfully with sessionId: ${sessionId}`);
        } catch (err) {
            console.error(`Error creating cart: ${err.message}`);
        }
    };

    const uploadFromFile = async (e) => {
        setUploadImages(true);
        if (imagecount + e.target.files.length > 55) {
            alert("צרו קשר על מנת להעלות יותר מ55 תמונות");
            modal('hide', 'uploadOptions');
            return;
        }

        setLoader((prevState) => ({
            ...prevState,
            showLoader: true,
            show50: true,
        }));

        // Check if sessionId exists, create one if it doesn't
        let sessionId = localStorage.getItem("sessionId");
        if (!sessionId) {
            // Generate a new sessionId
            sessionId = await generateSessionId();
            localStorage.setItem("sessionId", sessionId);
            console.log(`Generated new sessionId: ${sessionId}`);

            // Create a cart in the addtocart collection
            await createCart(uid, sessionId);
        }

        const frametype = localStorage.getItem("frameSelected");

        hideUploadOptions();
        navigate("/review-your-images");

        // Track image upload event for conversion tracking
        const uploadCount = e.target.files.length;

        // GA4 - for Google Ads attribution
        if (window.gtag) {
            window.gtag('event', 'image_upload', {
                event_category: 'engagement',
                event_label: 'file_upload',
                value: uploadCount,
                image_count: uploadCount
            });
        }

        // Meta Pixel
        if (window.fbq) {
            window.fbq('trackCustom', 'ImageUpload', {
                content_type: 'product',
                num_images: uploadCount
            });
        }

        // TikTok Pixel
        if (window.ttq) {
            window.ttq.track('ViewContent', {
                content_id: 'image_upload',
                content_type: 'product',
                content_name: 'Image Upload',
                quantity: uploadCount
            });
        }

        // Snapshot the picked files — the input can be cleared while we process.
        const batch = Array.from(e.target.files).map((file, i) => ({
            file,
            fileUniqueId: uniqid(),
            fileUploadedAt: new Date().getTime() + i,
        }));

        // One placeholder tile per file, pushed in a SINGLE state update, so the
        // whole batch appears at once (per-tile spinner via `processing`) instead
        // of trickling in one by one as each file finishes face detection. This
        // also counts the ENTIRE batch in imagesBlob.length immediately, so the
        // checkout gate (isImagesLoading) covers files that haven't reached
        // their turn in the processing queue yet.
        setImagesBlob((prevState) => [
            ...prevState,
            ...batch.map((it) => ({
                url: null,
                id: it.fileUniqueId,
                uploadedAt: it.fileUploadedAt,
                processing: true,
                faceDetected: false,
                // Sync-state contract: the tile renders as a plain white frame +
                // gray spinner until the SERVER acks the upload (getImagesDB then
                // removes this entry and the real server tile appears).
                uploadStatus: 'pending',
            })),
        ]);

        // Bounded-concurrency pool (same batcher pattern as the wall page): at
        // most 3 files hold decoded bitmaps at once, no matter how many were
        // picked — a large pick never OOMs iOS Safari.
        const BATCH_CONCURRENCY = 4;
        const runPool = async (count, worker) => {
            let next = 0;
            const runner = async () => {
                while (next < count) {
                    const cur = next++;
                    // eslint-disable-next-line no-await-in-loop
                    await worker(cur);
                }
            };
            await Promise.all(Array.from({ length: Math.min(BATCH_CONCURRENCY, count) }, runner));
        };

        // Reveal contract: ALL tiles appear together, every one already at its
        // final face-centered crop — zero visual jump. The spinner stays up
        // until the last photo's detection is done (analysis runs on a
        // <=1200px canvas so this is a few seconds for a full pick).
        const previewPatches = new Map(); // best-available preview per tile id
        // Reveal is IMMEDIATE (owner call): applyPreview writes straight to the
        // tiles, so each blurred preview appears the moment its file is decoded
        // instead of the whole batch waiting behind one spinner. The blur on
        // pending tiles masks the center-crop -> face-crop shift.
        let batchRevealed = true;
        const revealAll = () => {
            if (batchRevealed) return;
            batchRevealed = true;
            // Functional + patch-by-id only: getImagesDB (runs after each
            // upload) or the error filter may have removed entries — never
            // resurrect them, never touch other batches' tiles.
            setImagesBlob((prevState) =>
                prevState.map((b) =>
                    previewPatches.has(b.id)
                        ? { ...b, ...previewPatches.get(b.id), processing: false }
                        : b
                )
            );
        };
        const applyPreview = (id, patch) => {
            previewPatches.set(id, { ...(previewPatches.get(id) || {}), ...patch });
            if (batchRevealed) {
                setImagesBlob((prevState) =>
                    prevState.map((b) => (b.id === id ? { ...b, ...patch, processing: false } : b))
                );
            }
        };

        // PHASE A — downscale every file first (fast decode + re-encode).
        // Downscaling BEFORE face detection keeps every downstream coordinate
        // (crop box, zoom, preview, upload dims) on the exact bitmap that gets
        // uploaded. HEIC can't decode in-browser -> passes through untouched;
        // the server converts it. The plain preview stored here is the reveal
        // fallback if detection outruns the deadline.
        // Natural order — first-picked-first. (Was last-first while the strip
        // auto-scrolled to the end; with no jump the viewer watches the START,
        // so the visible tiles are the first picks again.)
        const procIdx = (cur) => cur;

        const prepped = [];
        await runPool(batch.length, async (cur) => {
            const idx = procIdx(cur);
            const item = batch[idx];
            try {
                const d = await downscaleForUpload(item.file);
                prepped[idx] = { item, file: d.file };
                // NO preview url yet (owner spec: the photo must enter ONCE,
                // already face-centered — showing the uncropped phase-A bitmap
                // and re-cropping it after detection made the image jump).
                applyPreview(item.fileUniqueId, {
                    imagewidth: d.w || undefined,
                    imageheight: d.h || undefined,
                });
            } catch (err) {
                console.error(`[UploadOptions] Downscale failed, using original: ${err.message}`);
                prepped[idx] = { item, file: item.file };
            }
        });

        // PHASE B — face detection + upload per file (same pool). Each file's
        // upload fires as soon as its own detection is done; the reveal never
        // delays uploads.
        const processFile = async (cur) => {
            const { item, file: newFile } = prepped[cur];
            const { fileUniqueId, fileUploadedAt } = item;

            // Create face-centered crop for instant preview
            let clientCropData = null;
            let calculatedZoomValue = 0;  // Pre-calculated zoom for EditImage modal
            try {
                const cropResult = await createFaceCenteredCrop(newFile);

                // Store crop data to send to server
                clientCropData = cropResult.cropData;

                // Include cropbox_data format so EditImage modal can read zoomFactor

                // Pre-calculate actual zoom value for EditImage modal
                // This avoids async state timing issues in the modal
                const zoomFactor = cropResult.cropData?.zoomFactor || 1.0;
                if (zoomFactor > 1.0 && cropResult.cropData?.originalWidth && cropResult.cropData?.originalHeight) {
                    const containerSize = 252; // EditImage modal cropper size
                    const imgWidth = cropResult.cropData.originalWidth;
                    const imgHeight = cropResult.cropData.originalHeight;
                    // minZoom = containerSize / smaller dimension (how cropper calculates it)
                    const minZoom = containerSize / Math.min(imgWidth, imgHeight);
                    calculatedZoomValue = minZoom * zoomFactor;
                    console.log(`[UploadOptions] Pre-calculated zoom: minZoom=${minZoom.toFixed(4)} * factor=${zoomFactor.toFixed(2)} = ${calculatedZoomValue.toFixed(4)}`);
                }

                applyPreview(fileUniqueId, {
                    url: cropResult.url,  // Face-centered crop blob URL
                    faceDetected: cropResult.cropData?.faceDetected || false,
                    cropData: cropResult.cropData,  // Store for reference
                    // Add cropbox_data format for EditImage modal compatibility
                    cropbox_data: cropResult.cropData ? {
                        left: cropResult.cropData.x,
                        top: cropResult.cropData.y,
                        width: cropResult.cropData.size,
                        height: cropResult.cropData.size,
                        zoomFactor: zoomFactor,
                        viewport: cropResult.cropData.viewport, // The actual viewport used for cropping
                    } : null,
                    zoomvalue: calculatedZoomValue,  // Pre-calculated zoom for modal
                    imagewidth: cropResult.cropData?.originalWidth,
                    imageheight: cropResult.cropData?.originalHeight,
                });

                console.log(`[UploadOptions] Face-centered preview created for file ${cur + 1}/${batch.length}`, cropResult.cropData);
            } catch (faceError) {
                // Face detection failed — the plain downscaled preview stored in
                // phase A stays as this tile's image.
                console.warn(`[UploadOptions] Face detection failed, using original: ${faceError.message}`);
                // Detection failed — show the plain downscaled bitmap as this
                // tile's one-and-only preview (center crop; still enters once).
                applyPreview(fileUniqueId, {
                    url: URL.createObjectURL(newFile),
                    faceDetected: false,
                });
            }

            // Upload the original file to the server. This <img> is used ONLY to read
            // pixel dimensions for the payload — it must NOT gate the actual POST.
            // HEIC (the default iPhone format) does not decode in browsers, so
            // img.onload never fires; previously the upload was silently dropped
            // (blank frames on desktop + "no image upload" in prod logs). Fire the
            // upload on BOTH onload and onerror so a raw HEIC still reaches the server,
            // which converts HEIC→JPEG and recomputes the real dimensions.
            const cropDataForUpload = clientCropData;
            const zoomValueForUpload = calculatedZoomValue;
            const measure = document.createElement("img");
            const measureUrl = URL.createObjectURL(newFile);
            let uploadFired = false;

            const doUpload = async (imgWidth, imgHeight) => {
                if (uploadFired) return;
                uploadFired = true;

                // Get email from localStorage if available
                const userEmail = localStorage.getItem("userEmail") || "";

                // newFile is already the phase-A downscaled bitmap (2000px short-edge
                // cap) — the transfer shrink happened there; no re-encode here.
                const buildFormData = () => {
                    const formdata = new FormData();
                    formdata.append("image", newFile);
                    formdata.append("uid", uid);
                    formdata.append("email", userEmail);
                    formdata.append("uniqueId", fileUniqueId);
                    formdata.append("uploadedAtTime", fileUploadedAt);
                    formdata.append("imagewidth", imgWidth || 0);
                    formdata.append("imageheight", imgHeight || 0);
                    formdata.append("imageext", 0);
                    formdata.append("frametype", frametype);
                    formdata.append("sessionId", sessionId);
                    // Send pre-calculated zoom value (for border avoidance zoom)
                    formdata.append("zoomvalue", zoomValueForUpload || 0);

                    // Send client-side crop coordinates to server when face detection
                    // succeeded. A HEIC has none (it can't decode in-browser) -> the server
                    // runs its own crop on the converted JPEG.
                    if (cropDataForUpload) {
                        const contentBounds = cropDataForUpload.contentBounds;
                        formdata.append("clientCropData", JSON.stringify({
                            left: cropDataForUpload.x,
                            top: cropDataForUpload.y,
                            width: cropDataForUpload.size,
                            height: cropDataForUpload.size,
                            faceDetected: cropDataForUpload.faceDetected,
                            faceCount: cropDataForUpload.faceCount || 0,
                            zoomFactor: cropDataForUpload.zoomFactor || 1.0,
                            contentBounds: contentBounds || null,
                            borderTypes: contentBounds?.borderTypes || null, // Include border types (black/white/mixed)
                            viewport: cropDataForUpload.viewport || null, // Include viewport for accurate zoom positioning
                            source: 'client-face-detection'
                        }));
                    }
                    return formdata;
                };

                enqueueResilientUpload({
                    id: fileUniqueId,
                    meta: {
                        page: "classic",
                        filename: newFile.name || "",
                        sizeKB: Math.round((newFile.size || 0) / 1024),
                        sessionId,
                        uid,
                    },
                    attempt: ({ signal, onUploadProgress, attemptNo }) => {
                        if (localStorage.getItem("orderId")) localStorage.removeItem("orderId");
                        return axios.post(`${uploadBaseForAttempt(attemptNo)}/imageupload_single`, buildFormData(), {
                            headers: { "content-type": "multipart/form-data" },
                            signal,
                            onUploadProgress,
                        });
                    },
                    onDone: async () => {
                        localStorage.setItem('dataExist', 1);
                        // The upload is ACKED — but this refresh can fail while the
                        // network is still settling (upload succeeded during a
                        // 4G->WiFi flap). Retry it, then ALWAYS clear the white
                        // frame: an acked upload must never keep spinning.
                        for (let i = 0; i < 3; i++) {
                            try { await getImagesDB(); break; }
                            catch (e) { await new Promise((r) => setTimeout(r, 2000 * (i + 1))); }
                        }
                        removeBlobEntry(fileUniqueId);
                    },
                    // Couldn't complete within the window — remove the white frame
                    // (same outcome a refresh would give).
                    onDrop: () => removeBlobEntry(fileUniqueId),
                });
            };

            measure.onload = function () {
                doUpload(measure.width, measure.height);
                URL.revokeObjectURL(measureUrl);
            };
            // HEIC / undecodable in the browser -> onload never fires. Upload anyway;
            // the server converts it and measures the real dimensions.
            measure.onerror = function () {
                doUpload(0, 0);
                // keep measureUrl for the error tile's thumbnail (if any); it's freed on unmount
            };
            measure.src = measureUrl;
        };

        // Every processFile failure is caught, so a dead file can't leave a
        // stuck spinner or block checkout forever — it's dropped after reveal.
        const failedIds = new Set();
        await runPool(batch.length, async (cur) => {
            const idx = procIdx(cur); // last-picked-first, same as phase A
            try {
                await processFile(idx);
            } catch (err) {
                console.error(`[UploadOptions] Processing failed for file ${idx + 1}: ${err.message}`);
                failedIds.add(batch[idx].fileUniqueId);
            }
        });
        // Whole batch face-centered -> single reveal, every tile at its final crop.
        revealAll();
        if (failedIds.size) {
            setImagesBlob((prevState) => prevState.filter((b) => !failedIds.has(b.id)));
        }

        /*if (e.target.files.length === 1) {

            let img = document.createElement("img");

            const newFile = e.target.files[0];
            const fileUniqueId = uniqid();
            const fileUploadedAt = new Date().getTime();
            img.src = URL.createObjectURL(newFile);

            setImagesBlob((prevState) => [
                ...prevState,
                {
                    url: img.src,
                    id: fileUniqueId,
                    uploadedAt: fileUploadedAt,
                },
            ]);

            img.onload = function () {
                formdata.append("image", newFile);
                formdata.append("uid", uid);
                formdata.append("uniqueId", fileUniqueId);
                formdata.append("uploadedAtTime", fileUploadedAt);
                formdata.append("imagewidth", img.width);
                formdata.append("imageheight", img.height);
                formdata.append("imageext", 0);
                formdata.append("frametype", frametype);

                uploadSingleImage(formdata)
                    .then((res) => {
                        /!*console.log(res);*!/
                        localStorage.setItem('dataExist', 1);

                        if(imagesBlob > 0){

                        }
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            };
        } else {
            let promises = [];
            for (let i = 0; i < e.target.files.length; i++) {

                promises.push(new Promise((res, _rej) => {
                    let files = e.target.files[i];
                    let img = document.createElement("img");
                    const fileUniqueId = uniqid();
                    const fileUploadedAt = new Date().getTime() + i;
                    img.src = URL.createObjectURL(files);

                    setImagesBlob((oldImagesBlob) => [
                        ...oldImagesBlob,
                        {
                            url: img.src,
                            id: fileUniqueId,
                            uploadedAt: fileUploadedAt,
                        },
                    ]);

                    img.onload = function () {
                        formdata.append("imagewidth", img.width);
                        formdata.append("imageheight", img.height);
                        formdata.append("imageext", 0);
                        formdata.append("image", files);
                        formdata.append("uniqueId", fileUniqueId);
                        formdata.append("uploadedAtTime", fileUploadedAt);
                        formdata.append("uid", uid);
                        formdata.append("frametype", frametype);
                        formdata.append("source", '151122');
                        res();
                    };
                }))
            }

            //https://stickable-admin.yeshostings.com
            Promise.all(promises).then(() => uploadMultipleImages(formdata));
        }*/
    };

    const uploadOptions = {
        accept: ["image/*"],
        maxFiles: 55 - imagecount,
        uploadInBackground: false,
        onUploadDone: async (res) => {
            const newImages = blobs(res);
            const uploadCount = newImages.length;

            // Track image upload event for conversion tracking (social uploads)
            // GA4 - for Google Ads attribution
            if (window.gtag) {
                window.gtag('event', 'image_upload', {
                    event_category: 'engagement',
                    event_label: 'social_upload',
                    value: uploadCount,
                    image_count: uploadCount
                });
            }

            // Meta Pixel
            if (window.fbq) {
                window.fbq('trackCustom', 'ImageUpload', {
                    content_type: 'product',
                    num_images: uploadCount
                });
            }

            // TikTok Pixel
            if (window.ttq) {
                window.ttq.track('ViewContent', {
                    content_id: 'image_upload',
                    content_type: 'product',
                    content_name: 'Image Upload',
                    quantity: uploadCount
                });
            }

            // Get email from localStorage if available
            const userEmail = localStorage.getItem("userEmail") || "";

            res.uid = uid;
            res.email = userEmail; // Add email to the request

            // Check if sessionId exists, create one if it doesn't
            let sessionId = localStorage.getItem("sessionId");
            if (!sessionId) {
                // Generate a new sessionId
                sessionId = await generateSessionId();
                localStorage.setItem("sessionId", sessionId);
                console.log(`Generated new sessionId: ${sessionId}`);

                // Create a cart in the addtocart collection
                await createCart(uid, sessionId);
            }

            // Add sessionId to the request
            res.sessionId = sessionId;

            setLoader((prevState) => ({
                ...prevState,
                showLoader: true,
                show50: true,
            }));
            navigate("/review-your-images");
            await addImageFromSocial(res);
        },
    };

    const facebookPhotoImport = () => {
        hideUploadOptions();
        const options = {
            fromSources: ["facebook"],
            ...uploadOptions
        }

        filestack_client.picker(options).open();
    };

    const instagramPhotoImport = () => {
        hideUploadOptions();
        const options = {
            fromSources: ["instagram"],
            ...uploadOptions
        };

        filestack_client.picker(options).open();
    };

    const googlePhotosImport = () => {
        hideUploadOptions();
        const options = {
            fromSources: ["googlephotos"],
            ...uploadOptions
        };

        filestack_client.picker(options).open();
    };

    return (
        <>
        <UploadErrorSnackbar errors={uploadErrors} onDismiss={() => setUploadErrors([])} />
        <div className={cn("upload-options", {open: isOpen})} style={{...style}}>
            <div className="upload-options__item" style={{fontWeight: "500"}}>
                בחירת תמונות מהמכשיר<img src="/assets/file/images/upload-file.svg" alt="file"/>
                {isOpen && <input
                    className="file-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => uploadFromFile(e)}
                />}
            </div>
            <button onClick={() => googlePhotosImport()} className="upload-options__item"
                    style={{color: "black", fontWeight: "500"}}>
                ייבוא מגוגל פוטוס <img src="/assets/file/images/upload-google.svg" alt="google"/>
            </button>
            {/* <button onClick={() => facebookPhotoImport()} className="upload-options__item"
                    style={{color: "black", fontWeight: "500"}}>
                ייבוא מהפייסבוק <img src="/assets/file/images/upload-fb.svg" alt="facebook"/>
            </button> */}
            <button onClick={() => instagramPhotoImport()} className="upload-options__item"
                    style={{color: "black", fontWeight: "500"}}>
                ייבוא מהאינסטגרם <img src="/assets/file/images/upload-inst.svg" alt="instagram"/>
            </button>
        </div>
        </>
    );
};

export default UploadOptions;
